import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, Calculator, TrendingDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import SeoHead from '@/components/shared/SeoHead';
import PageHero from '@/components/shared/PageHero';
import PageBreadcrumb from '@/components/shared/PageBreadcrumb';
import AdSlot from '@/components/shared/AdSlot';
import AdFrame from '@/components/shared/AdFrame';
import { RectangleAd, InContentAd } from '@/components/shared/AdSense';

interface TaxData {
  annual_income: number;
  federal_tax: number;
  state_tax: number;
  social_security: number;
  medicare: number;
  total_taxes: number;
  net_income: number;
  effective_federal_rate: number;
  effective_state_rate: number;
  effective_total_rate: number;
  take_home_pay: {
    weekly: number;
    bi_weekly: number;
    monthly: number;
    annual: number;
  };
}

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

const TaxCalculator = () => {
  const [salary, setSalary] = useState<number>(0);
  const [state, setState] = useState<string>("");
  const [enabled, setEnabled] = useState(false);

  const { data: taxData, isLoading, error } = useQuery<TaxData>({
    queryKey: ['tax-calculator-standalone', salary, state],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-tax-calculator', {
        body: {
          salary: salary,
          state: state,
          filing_status: 'single'
        }
      });

      if (error) throw error;
      return data;
    },
    enabled: enabled && salary > 0 && state !== ""
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  return (
    <>
      <SeoHead
        title="Tax Calculator - Federal & State Income Tax Calculator | Wage Atlas"
        description="Free tax calculator to estimate federal and state income taxes. Calculate your take-home pay and effective tax rates across all 50 states."
        
        occupation="Tax Calculator"
        canonical="/calculators/tax/"
      />
      
      <div className="min-h-screen bg-background">
        <PageHero
          title="Tax Calculator"
          subtitle="Calculate your federal and state income taxes, see your take-home pay, and compare effective tax rates across states."
        >
          <AdFrame className="flex justify-center">
            <AdSlot slot="leaderboard" className="max-w-4xl" />
          </AdFrame>
        </PageHero>

        {/* Calculator */}
        <section className="py-8 px-4">
          <div className="max-w-6xl mx-auto mb-6">
            <PageBreadcrumb
              items={[
                { label: 'Home', href: '/' },
                { label: 'Calculators', href: '/calculators' },
                { label: 'Tax Calculator' },
              ]}
            />
          </div>
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 grid md:grid-cols-2 gap-8">
            {/* Input Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-primary" />
                  Tax Calculator
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="salary">Annual Salary</Label>
                  <Input
                    id="salary"
                    type="number"
                    placeholder="Enter annual salary"
                    value={salary || ""}
                    onChange={(e) => setSalary(parseInt(e.target.value) || 0)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((stateOption) => (
                        <SelectItem key={stateOption.value} value={stateOption.value}>
                          {stateOption.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="tax-toggle"
                    checked={enabled}
                    onCheckedChange={setEnabled}
                  />
                  <Label htmlFor="tax-toggle" className="text-sm">Calculate Taxes</Label>
                </div>

                {error && (
                  <div className="text-red-500 text-sm">
                    Error calculating taxes. Please try again.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  Tax Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!enabled ? (
                  <p className="text-muted-foreground text-center py-8">
                    Enable the tax calculator to see your tax breakdown
                  </p>
                ) : isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : taxData ? (
                  <div className="space-y-6">
                    {/* Tax Breakdown */}
                    <div className="space-y-3">
                      <h4 className="font-medium">Annual Tax Breakdown</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Federal Tax:</span>
                          <span className="text-red-600">{formatCurrency(taxData.federal_tax)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>State Tax:</span>
                          <span className="text-red-600">{formatCurrency(taxData.state_tax)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Social Security:</span>
                          <span className="text-red-600">{formatCurrency(taxData.social_security)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Medicare:</span>
                          <span className="text-red-600">{formatCurrency(taxData.medicare)}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between font-medium">
                          <span>Total Taxes:</span>
                          <span className="text-red-600">{formatCurrency(taxData.total_taxes)}</span>
                        </div>
                        <div className="flex justify-between font-medium text-lg">
                          <span>Net Income:</span>
                          <span className="text-green-600">{formatCurrency(taxData.net_income)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Effective Rates */}
                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <TrendingDown className="h-4 w-4" />
                        Effective Tax Rates
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Federal Rate:</span>
                          <span>{formatPercentage(taxData.effective_federal_rate)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>State Rate:</span>
                          <span>{formatPercentage(taxData.effective_state_rate)}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between font-medium">
                          <span>Total Rate:</span>
                          <span>{formatPercentage(taxData.effective_total_rate)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Take Home Pay */}
                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Take-Home Pay
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Weekly:</span>
                          <span className="text-green-600">{formatCurrency(taxData.take_home_pay.weekly)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Bi-weekly:</span>
                          <span className="text-green-600">{formatCurrency(taxData.take_home_pay.bi_weekly)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Monthly:</span>
                          <span className="text-green-600">{formatCurrency(taxData.take_home_pay.monthly)}</span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span>Annual:</span>
                          <span className="text-green-600">{formatCurrency(taxData.take_home_pay.annual)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    Enter your salary and select a state to calculate taxes
                  </p>
                )}
              </CardContent>
            </Card>
            </div>

            {/* Sidebar Ad */}
            <div className="hidden lg:block">
              <RectangleAd className="sticky top-4" />
            </div>
          </div>

          {/* In-Content Ad */}
          <AdFrame className="max-w-4xl mx-auto mt-8">
            <InContentAd />
          </AdFrame>
        </section>

        {/* More Calculators */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-8">More Calculators</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent>
                  <Calculator className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">Salary Calculator</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Convert between hourly and annual wages with comparable job data
                  </p>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/calculators/salary">Try Salary Calculator</Link>
                  </Button>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-lg transition-shadow">
                <CardContent>
                  <DollarSign className="h-8 w-8 text-primary mx-auto mb-3" />
                  <h3 className="font-semibold mb-2">All Calculators</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Browse all our financial planning tools and calculators
                  </p>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/calculators">View All Calculators</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TaxCalculator;